import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { isConversationsOnlyStaff } from "./nav-config";
import { PageHeader } from "../../components/PageHeader";
import { useI18n } from "@wato/i18n/react";
import { Stack } from "@wato/ui";
import { GetStartedCard } from "../home/GetStartedCard";
import { NextStepCard } from "../home/NextStepCard";
import { CardBoundary } from "../../components/CardBoundary";
import { HomeKpis } from "../home/HomeKpis";
import { ModulesPrompt } from "../settings/workspace/ModulesPrompt";
import { ProfileHome } from "../home/ProfileHome";
import { HomeQuickActions } from "../home/HomeQuickActions";
import { HomeContactsGrowth } from "../home/HomeContactsGrowth";
import { HomeAiStatus } from "../home/HomeAiStatus";
import { HomeAssistantCard } from "../home/HomeAssistantCard";
import { HomeAttention, HomeCreateChips } from "../home/HomeAttention";
import { HomeFlowErrors } from "../home/HomeFlowErrors";
import { HomeRecentChats } from "../home/HomeRecentChats";
import { HomeReports } from "../home/HomeReports";
import { PageHelp } from "../../components/PageHelp";
import { UpsellPrompt } from "../onboarding/UpsellPrompt";
import { useApi } from "../../api/context";
import { mayUse } from "../roles/role-label";
import { useWorkspace } from "./WorkspaceContext";

/** The workspace's (still empty) dashboard — where a new Owner lands after creation. */
export function WorkspaceHome() {
  const { workspace: current } = useWorkspace();
  if (isConversationsOnlyStaff(current.permissions?.granted)) {
    return <Navigate to={`/w/${current.id}/conversations`} replace />;
  }
  return <WorkspaceHomeContent />;
}

function WorkspaceHomeContent() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const api = useApi();
  const canViewAnalytics = mayUse(workspace, "analytics.view");
  const canViewFlows = mayUse(workspace, "flows.view");
  // FB3-23: «nothing here yet» only when the real data (conversations, contacts, orders, flows,
  // today's overview) is empty; unknown (failed/unpermitted calls, nothing positive) shows nothing.
  const [hasActivity, setHasActivity] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    const day = new Date().toISOString().slice(0, 10);
    void Promise.allSettled([
      canViewAnalytics
        ? api
            .getAnalyticsOverview(workspace.id, { from: day, to: day })
            .then((o) => o.conversations.open > 0 || o.conversations.new > 0)
        : Promise.resolve(false),
      api.listConversations(workspace.id, { limit: 1 }).then((p) => p.items.length > 0),
      api.listContacts(workspace.id, { limit: 1 }).then((p) => p.items.length > 0),
      api.listWaOrders(workspace.id).then((o) => o.length > 0),
      // Q4-ONB-08-a: a role without flows.view must not fire a request the API refuses (403).
      canViewFlows ? api.listFlows(workspace.id).then((f) => f.length > 0) : Promise.resolve(false),
    ]).then((results) => {
      if (!live) return;
      const found = results.some((r) => r.status === "fulfilled" && r.value);
      const allKnown = results.every((r) => r.status === "fulfilled");
      setHasActivity(found ? true : allKnown ? false : null);
    });
    return () => {
      live = false;
    };
  }, [api, workspace.id, canViewAnalytics, canViewFlows]);
  return (
    <Stack as="div" gap={3}>
      <PageHeader
        title={t("dashboard.home.welcome", { name: workspace.name })}
        help={<PageHelp pageKey="home" />}
      />
      {hasActivity === false && (
        <p className="m-0 text-sm text-fg-muted" data-testid="home-empty-message">
          {t("dashboard.home.empty")}
        </p>
      )}
      <CardBoundary>
        <HomeAttention workspaceId={workspace.id} />
      </CardBoundary>
      <CardBoundary>
        <HomeFlowErrors workspaceId={workspace.id} />
      </CardBoundary>
      <CardBoundary>
        <HomeCreateChips workspaceId={workspace.id} />
      </CardBoundary>
      <div className="grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(20rem,1fr))]">
        <CardBoundary>
          <HomeRecentChats workspaceId={workspace.id} />
        </CardBoundary>
        {mayUse(workspace, "ai_agents.view") && (
          <CardBoundary>
            <HomeAiStatus workspaceId={workspace.id} />
          </CardBoundary>
        )}
      </div>
      <CardBoundary>
        <GetStartedCard workspaceId={workspace.id} />
      </CardBoundary>
      <div data-tour-target="home-next">
        <CardBoundary>
          <NextStepCard workspaceId={workspace.id} />
        </CardBoundary>
      </div>
      <CardBoundary>
        <ModulesPrompt variant="banner" />
      </CardBoundary>
      <CardBoundary>
        <HomeQuickActions workspaceId={workspace.id} />
      </CardBoundary>
      <CardBoundary>
        <HomeAssistantCard workspaceId={workspace.id} />
      </CardBoundary>
      <CardBoundary>
        <ProfileHome workspaceId={workspace.id} />
      </CardBoundary>
      <div data-tour-target="home-kpis">
        <CardBoundary>
          <HomeKpis workspaceId={workspace.id} />
        </CardBoundary>
      </div>
      {canViewAnalytics && (
        <CardBoundary>
          <HomeContactsGrowth workspaceId={workspace.id} />
        </CardBoundary>
      )}
      <CardBoundary>
        <UpsellPrompt workspaceId={workspace.id} />
      </CardBoundary>
      <CardBoundary>
        <HomeReports />
      </CardBoundary>
    </Stack>
  );
}
